import { gsap } from 'gsap/dist/gsap';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { Link } from 'components/link/Link';
import { MainFooter } from 'components/mainFooter/MainFooter';
import { GlobalDataType } from 'utils/parsers';

import style from './Menu.module.scss';

export type MenuType = {
  resetScroll: () => void;
  isOpen: Boolean;
  closeTimeoutMS: number;
  globalData: GlobalDataType;
  onShowComplete;
};

export const Menu = ({
  isOpen,
  closeTimeoutMS,
  onShowComplete,
  resetScroll,
  globalData
}: MenuType): JSX.Element => {
  const bg = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const footer = useRef<HTMLDivElement>(null);

  const openTimeline = gsap.timeline();
  const closeTimeline = gsap.timeline();

  const closingDuration = closeTimeoutMS / 1000;

  useEffect(() => {
    if (isOpen) {
      if (bg.current) {
        gsap.killTweensOf(bg.current);
        gsap.set(bg.current, { alpha: 0, transformOrigin: '100%' });
        openTimeline.to(bg.current, {
          alpha: 1,
          duration: 0.25,
          ease: 'power4.inOut',
          onComplete: () => {
            onShowComplete();
          },
        });
      }
      if (list.current) {
        gsap.utils.toArray(list.current.children).forEach((element: any, index) => {
          gsap.killTweensOf(element);
          gsap.set(element, { opacity: 0 });
          openTimeline.to(element, { opacity: 1, duration: 0.25 }, '<25%');
        });
      }
      if (footer.current) {
        gsap.utils.toArray(footer.current.children).forEach((element: any, index) => {
          gsap.killTweensOf(element);
          gsap.set(element, { opacity: 0 });
          openTimeline.to(element, { opacity: 1, duration: 0.15 }, '<');
        });
      }
    } else {
      // closing animation
      if (list.current) {
        gsap.utils.toArray(list.current.children).forEach((element: any, index) => {
          gsap.killTweensOf(element);
          closeTimeline.to(element, { opacity: 0, duration: 0.1 }, '<25%');
        });
      }
      if (footer.current) {
        gsap.utils.toArray(footer.current.children).forEach((element: any, index) => {
          gsap.killTweensOf(element);
          closeTimeline.to(element, { opacity: 0, duration: 0.1 }, '<%');
        });
      }
      if (bg.current) {
        gsap.killTweensOf(bg.current);
        closeTimeline.to(bg.current, {
          onStart: () => {
            resetScroll();
          },
          alpha: 0,
          duration: 0.15,
          ease: 'power4.inOut',
        });
      }

      closeTimeline.duration(closingDuration);
      closeTimeline.play();
    }
  });

  const { asPath } = useRouter();

  const links = [
    { href: '/', label: 'Work' },
    { href: '/about', label: 'About' },
    { href: '/contact', label: 'Connect' },
  ];

  return (
    <div className={style.menuContainer}>
      <div ref={bg} className={style.bg}></div>

      <ul ref={list} className={style.list}>
        {links.map((link) => {
          const itemState = link.href === asPath ? style.listItemLinkActive : null;
          return (
            <li className={`${style.listItem} `} key={link.label}>
              <Link href={link.href}>
                <a className={`${style.listItemLink} ${itemState}`}>{link.label}</a>
              </Link>
            </li>
          );
        })}
      </ul>
      <MainFooter {...globalData} />
    </div>
  );
};
